import { useEffect, useSyncExternalStore } from 'react'
import { Msg } from '../transcriptBlocks'
import type { TranscriptBlock } from '../../transport/api'
import { clearSentEcho, sentEchoFor, sentEchoVersion, subscribeSentEcho } from './sentEchoStore'
import { sameMessage } from './explained'

/**
 * The message the PERSON just sent, until the transcript holds it.
 *
 * Owner, 2026-09-16: 使用者输入的文字内容并没有即时的显示在 Agent 的 CleanView 里面 …
 * 使用者輸入的文字就全部都被吃掉了。當該 Agent 開始輸出之後, 使用者的文字以及 Agent
 * 本身的文字又可以被看到.
 *
 * The cause is in sentEchoStore.ts: an agent's composer posts no message row, and claude
 * writes its transcript when a message completes, so between send and reply there is
 * nothing on disk to draw — and when the file is written both halves arrive at once.
 *
 * Drawn through `Msg`, like LiveBlock beside it, for the owner's stated reason about
 * that one: 跟真的 block 完全一樣的樣式. It sits BETWEEN the settled blocks and the
 * agent's live block, which is where it belongs in time: after everything that has
 * happened, before the reply being written.
 *
 * IT HIDES ITSELF THE MOMENT THE TRANSCRIPT CAN SPEAK, and does not wait to be told.
 * `lastUser` is the newest user block the window holds; when that is the message this
 * echo is standing in for, the real row is on screen and this one would be a double.
 * The two are not byte-identical in practice — the CLI may wrap, trim, or prefix what it
 * records — so the match is explained.sameMessage: exact for a short message, containment
 * for one the CLI added to, coverage for a long one it rewrapped. Coverage ALONE was the
 * rule until 2026-09-29, and it cannot see under twelve characters: every short message
 * stayed on screen twice.
 */

/** Above this, the transcript's newest user block IS this echo. */
const SAME_MESSAGE = 0.8

export function SentEcho({
  session,
  hash,
  lastUser,
  resolve,
  describeHash,
  describeRoom,
  ownerName,
  resolutionKey,
}: {
  session: string
  hash: string
  /** Raw text of the newest USER block the transcript already holds, or null. */
  lastUser: string | null
  resolve: (label: string) => string | null
  describeHash?: ((h: string) => { name: string; machine: string } | null) | null
  describeRoom?: ((id: string) => string | null) | null
  ownerName: string | null
  resolutionKey: string
}) {
  useSyncExternalStore(subscribeSentEcho, sentEchoVersion, () => 0)
  const text = sentEchoFor(session)
  /* The real record landed — this row is a duplicate from here on. */
  const handedOver = !!text && sameMessage(text, lastUser, SAME_MESSAGE)
  /*
   * RELEASED, not merely hidden. Returning null would leave the store holding text
   * the transcript can now speak for, which would reappear on any later render where
   * `lastUser` has scrolled out of the window. Dropping it is what makes the handover
   * one-way. In an effect because render must stay free of store writes.
   */
  useEffect(() => {
    if (handedOver) clearSentEcho()
  }, [handedOver])
  if (!text || handedOver) return null

  const b: TranscriptBlock = { kind: 'user', text }
  return (
    <Msg
      b={b}
      session={session}
      hash={hash}
      resolve={resolve}
      describeHash={describeHash ?? null}
      describeRoom={describeRoom ?? null}
      ownerName={ownerName}
      resolutionKey={resolutionKey}
    />
  )
}
